import { TOKENS } from "../lib/designTokens";
import { PageHeader } from "../components/PageHeader";
import { MetricCard } from "../components/layout/MetricCard";
import { SectionHeader } from "../components/layout/SectionHeader";
import { EmptyState } from "../components/layout/EmptyState";
import { SportBadge } from "../components/SportBadge";
import { StatusBadge } from "../components/StatusBadge";
import { useEffect, useMemo, useState } from "react";
import { CalendarDays, CalendarRange, ClipboardList } from "lucide-react";
import { listActivitiesInRange } from "../lib/api";
import { fetchCurrentActiveTrainingPlan } from "../api/trainingPlanApi";
import { ACTIVE_PLAN_CHANGED_EVENT } from "../lib/activePlanSync";
import { PlannedSessionEditor } from "../components/coaching/PlannedSessionEditor";
import { SessionPrescriptionView } from "../components/coaching/SessionPrescriptionView";
import { WorkoutDownloadMenu } from "../components/coaching/WorkoutDownloadMenu";
import { formatDate, formatObjective, formatWeekType } from "../lib/coachingFormatters";
import { activePlanBlockDates, activePlanRestDateKeys, planDatedRows } from "../lib/activePlanDisplay";
import { deriveActivityName } from "../lib/activityName";
import { sportDisplayLabel } from "../lib/sportNormalization";
import { ActivityCard } from "../components/ActivityCard";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtDistance, fmtDurationShort } from "../lib/format";
import { useAuth } from "../lib/auth";
import { getMetricVisual } from "../lib/metricVisuals";
import {
  buildCalendarYearOptions,
  buildCalendarMonthOptions,
  calendarGridFetchRange,
  findDefaultCalendarMonth,
  getRideDatesInMonth,
  getActivitiesForDate,
  getActivitiesInMonth,
  groupActivitiesByDay,
  toDateKey,
} from "../lib/timeline";

function RideDayContent({ date, rideDateKeys, plannedDateKeys, restDateKeys }) {
  const key = toDateKey(date);
  const hasRide = key && rideDateKeys.has(key);
  const hasPlanned = key && plannedDateKeys.has(key);
  const hasRest = key && restDateKeys && restDateKeys.has(key) && !hasPlanned;
  return (
    <div className="flex flex-col items-center justify-center gap-0.5 leading-none">
      <span>{date.getDate()}</span>
      {(hasRide || hasPlanned || hasRest) && (
        <span className="flex items-center justify-center gap-0.5" aria-hidden="true">
          {hasRest && (
            <span
              className="w-1.5 h-1.5 rounded-full border border-dark-text-muted"
              title="Rest day"
              data-testid="calendar-rest-mark"
            />
          )}
          {hasRide && (
            <span
              className="w-1.5 h-1.5 rounded-full bg-brand-primary shadow-[0_0_0_1px_rgba(49,185,120,0.35)]"
              title="Recorded activity"
            />
          )}
          {hasPlanned && (
            <span
              className="w-1.5 h-1.5 rounded-full bg-analytics-amber shadow-[0_0_0_1px_rgba(217,154,43,0.35)]"
              data-testid={`planned-dot-${key}`}
              title="Planned session"
            />
          )}
        </span>
      )}
    </div>
  );
}

// Deterministic projection of active-plan sessions into a dateKey→[session] map.
// Uses only fields that already come from athlete_calendar_schedules.scheduled_sessions
// (session_id, scheduled_date, sport, duration_minutes, workout_code, weekday) so the
// calendar view and the AI Coach active-plan card render the SAME authoritative records.
function collectPlannedByDay(activePlan) {
  const map = new Map();
  if (!activePlan || !Array.isArray(activePlan.weeks)) return map;
  for (const week of activePlan.weeks) {
    for (const session of week.sessions || []) {
      const key = session.scheduled_date;
      if (!key) continue;
      const list = map.get(key) || [];
      list.push({ ...session, week_number: week.week_number });
      map.set(key, list);
    }
  }
  return map;
}

function collectPlanYears(activePlan) {
  const years = new Set();
  if (!activePlan) return years;
  for (const raw of [activePlan.block_start_date, activePlan.block_end_date]) {
    if (raw && String(raw).length >= 4) years.add(String(raw).slice(0, 4));
  }
  for (const week of activePlan.weeks || []) {
    for (const session of week.sessions || []) {
      const key = session.scheduled_date;
      if (key && String(key).length >= 4) years.add(String(key).slice(0, 4));
    }
  }
  return years;
}

function todayLocalDate() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function plannedDurationLabel(session) {
  const current = typeof session?.duration_minutes === "number" ? session.duration_minutes : null;
  const original = typeof session?.original_duration_minutes === "number"
    ? session.original_duration_minutes
    : null;
  if (current == null) return "";
  if (original != null && original !== current) {
    return `${original} min → ${current} min`;
  }
  return `${current} min`;
}

function sessionChangeLabel(session) {
  if (session?.adaptive_summary) return session.adaptive_summary;
  if (session?.athlete_edit_source === "ATHLETE_MANUAL_EDIT") return "Athlete adjusted";
  if (session?.difficulty_adjustment && session.difficulty_adjustment !== "AS_PLANNED") {
    return "Athlete adjusted";
  }
  return null;
}

function plannedStatusLabel(session) {
  if (session?.plan_status_label) return session.plan_status_label;
  if (session?.is_completed) return "Completed";
  return "Planned";
}

function sessionStatusVariant(session, label) {
  if (session?.is_completed) return "completed";
  const lower = String(label || "").toLowerCase();
  if (lower.includes("recovery")) return "recovery";
  if (session?.adaptive_action || session?.adaptive_non_actionable) return "load";
  if (session?.athlete_edit_source === "ATHLETE_MANUAL_EDIT") return "load";
  if (session?.difficulty_adjustment && session.difficulty_adjustment !== "AS_PLANNED") return "load";
  return "planned";
}

function formatWeekHeader(week) {
  const typeLabel = week.week_type
    ? formatWeekType(week.week_type).replace(/ week$/i, "")
    : null;
  return typeLabel
    ? `Week ${week.week_number} — ${typeLabel}`
    : `Week ${week.week_number}`;
}

function weekSessionStats(week) {
  const sessions = week.sessions || [];
  const count = sessions.length;
  const minutes = sessions.reduce(
    (acc, session) => acc + (typeof session.duration_minutes === "number" ? session.duration_minutes : 0),
    0,
  );
  return { count, minutes };
}

function findActivityById(activities, activityId) {
  if (!activityId) return null;
  return (activities || []).find((activity) => String(activity.id) === String(activityId)) || null;
}

export const CalendarPage = ({ refreshKey = 0 }) => {
  const { user } = useAuth();
  const unitSystem = user?.unit_system || "metric";
  const initialToday = todayLocalDate();
  const [activities, setActivities] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(() => String(initialToday.getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(() => String(initialToday.getMonth()));
  const [selectedDate, setSelectedDate] = useState(null);

  const activitiesMetric = getMetricVisual("brand");
  const distanceMetric = getMetricVisual("distance");
  const durationMetric = getMetricVisual("duration");

  const reloadActivePlan = () => {
    fetchCurrentActiveTrainingPlan()
      .then((plan) => setActivePlan(plan || null))
      .catch(() => setActivePlan(null));
  };

  useEffect(() => {
    const onPlanChanged = () => reloadActivePlan();
    window.addEventListener(ACTIVE_PLAN_CHANGED_EVENT, onPlanChanged);
    return () => window.removeEventListener(ACTIVE_PLAN_CHANGED_EVENT, onPlanChanged);
  }, []);

  const fetchMonthActivities = () => {
    if (selectedYear == null || selectedMonth == null) {
      return Promise.resolve();
    }
    const { startTime, endTime } = calendarGridFetchRange(
      Number(selectedYear),
      Number(selectedMonth),
    );
    setActivitiesLoading(true);
    setActivitiesError(null);
    return listActivitiesInRange({ startTime, endTime })
      .then((data) => setActivities(data || []))
      .catch((err) => {
        setActivities([]);
        if (err.isAuthError) setActivitiesError("session");
        else if (!err.response) setActivitiesError("network");
        else setActivitiesError("server");
      })
      .finally(() => setActivitiesLoading(false));
  };

  useEffect(() => {
    reloadActivePlan();
  }, [refreshKey]);

  useEffect(() => {
    if (selectedYear == null || selectedMonth == null) return undefined;

    let cancelled = false;
    const controller = new AbortController();
    const { startTime, endTime } = calendarGridFetchRange(
      Number(selectedYear),
      Number(selectedMonth),
    );

    setActivitiesLoading(true);
    setActivitiesError(null);
    listActivitiesInRange({ startTime, endTime }, { signal: controller.signal })
      .then((data) => {
        if (!cancelled) setActivities(data || []);
      })
      .catch((err) => {
        if (cancelled || err?.code === "ERR_CANCELED") return;
        setActivities([]);
        if (err.isAuthError) setActivitiesError("session");
        else if (!err.response) setActivitiesError("network");
        else setActivitiesError("server");
      })
      .finally(() => {
        if (!cancelled) setActivitiesLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [selectedYear, selectedMonth, refreshKey]);

  const yearOptions = useMemo(() => {
    const years = new Set();
    const currentYear = todayLocalDate().getFullYear();
    for (let year = currentYear; year >= currentYear - 10; year -= 1) {
      years.add(String(year));
    }
    collectPlanYears(activePlan).forEach((year) => years.add(year));
    buildCalendarYearOptions(activities).forEach((option) => years.add(option.value));
    return Array.from(years)
      .sort((a, b) => Number(b) - Number(a))
      .map((year) => ({ value: String(year), label: String(year) }));
  }, [activities, activePlan]);

  const monthOptions = useMemo(
    () => (selectedYear ? buildCalendarMonthOptions(selectedYear) : []),
    [selectedYear]
  );

  useEffect(() => {
    if (selectedYear == null || selectedMonth == null) return;
    const today = todayLocalDate();
    const viewingCurrentMonth = (
      Number(selectedYear) === today.getFullYear()
      && Number(selectedMonth) === today.getMonth()
    );
    if (viewingCurrentMonth && selectedDate == null) {
      setSelectedDate(today);
    }
  }, [selectedYear, selectedMonth, selectedDate]);

  const displayMonth = useMemo(() => {
    if (selectedYear == null || selectedMonth == null) return new Date();
    return new Date(Number(selectedYear), Number(selectedMonth), 1);
  }, [selectedYear, selectedMonth]);

  const plannedByDay = useMemo(() => collectPlannedByDay(activePlan), [activePlan]);
  const plannedDateKeys = useMemo(
    () => new Set(plannedByDay.keys()),
    [plannedByDay],
  );
  const restDateKeys = useMemo(
    () => activePlanRestDateKeys(activePlan),
    [activePlan],
  );

  const rideDateKeys = useMemo(() => {
    if (selectedYear == null || selectedMonth == null) return new Set();
    const dates = getRideDatesInMonth(
      activities,
      Number(selectedYear),
      Number(selectedMonth)
    );
    return new Set(dates.map((d) => toDateKey(d)));
  }, [activities, selectedYear, selectedMonth]);

  const monthActivities = useMemo(() => {
    if (selectedYear == null || selectedMonth == null) return [];
    return getActivitiesInMonth(activities, Number(selectedYear), Number(selectedMonth));
  }, [activities, selectedYear, selectedMonth]);

  const monthTotals = useMemo(() => ({
    count: monthActivities.length,
    distance_m: monthActivities.reduce((s, a) => s + (a.total_distance_m || 0), 0),
    duration_s: monthActivities.reduce((s, a) => s + (a.duration_seconds || 0), 0),
  }), [monthActivities]);

  const activitiesByDay = useMemo(() => groupActivitiesByDay(activities), [activities]);

  const dayActivities = useMemo(() => {
    if (!selectedDate) return [];
    return getActivitiesForDate(activities, selectedDate, activitiesByDay);
  }, [activities, selectedDate, activitiesByDay]);

  const selectedDateKey = selectedDate ? toDateKey(selectedDate) : null;
  const dayPlannedSessions = selectedDateKey
    ? (plannedByDay.get(selectedDateKey) || [])
    : [];

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setSelectedMonth((prev) => prev ?? findDefaultCalendarMonth(activities, year));
    setSelectedDate(null);
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    setSelectedDate(null);
  };

  const selectedMonthLabel = monthOptions.find((m) => m.value === selectedMonth)?.label;
  const hasActivePlan = Boolean(activePlan && Array.isArray(activePlan.weeks) && activePlan.weeks.length > 0);

  return (
    <div className="min-w-0">
      <PageHeader
        eyebrow="Training"
        title="Calendar"
        description={
          <>
            See which days include recorded activities and planned sessions from your
            active training plan. Days with a recorded activity show a{" "}
            <span className="text-brand-primary font-medium">green dot</span>; days with a planned
            session show an <span className="text-analytics-amber font-medium">amber dot</span>.
            Select a date to view that day&apos;s planned training and recorded activities.
          </>
        }
      />

      <>
          <section
            className="app-card mb-8 overflow-hidden"
            data-testid="calendar-controls"
          >
            <div className="p-4 sm:p-6 border-b border-dark-border flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="icon-frame w-10 h-10 shrink-0"
                  style={{
                    color: TOKENS.brand.primary,
                    background: "rgba(49, 185, 120, 0.12)",
                    borderColor: "rgba(49, 185, 120, 0.35)",
                  }}
                >
                  <CalendarDays className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-heading text-xl sm:text-2xl tracking-wider uppercase text-dark-text-primary truncate">
                    {selectedMonthLabel} {selectedYear}
                  </h2>
                  <p className="text-xs text-dark-text-muted mt-0.5">
                    Monthly training overview
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <Select value={selectedYear ?? ""} onValueChange={handleYearChange}>
                  <SelectTrigger
                    className="w-[110px] sm:w-[120px] bg-dark-background border-dark-border text-xs tracking-widest uppercase"
                    data-testid="calendar-year-select"
                  >
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent className="bg-dark-surface-1 border-dark-border">
                    {yearOptions.map((y) => (
                      <SelectItem key={y.value} value={y.value} className="text-xs">
                        {y.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedMonth ?? ""} onValueChange={handleMonthChange}>
                  <SelectTrigger
                    className="w-[140px] sm:w-[160px] bg-dark-background border-dark-border text-xs tracking-wide"
                    data-testid="calendar-month-select"
                  >
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent className="bg-dark-surface-1 border-dark-border">
                    {monthOptions.map((m) => (
                      <SelectItem key={m.value} value={m.value} className="text-xs">
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {activitiesError && (
              <div
                className="mx-4 sm:mx-6 mt-4 rounded-md border border-analytics-amber/40 bg-analytics-amber/10 px-3 py-2 text-xs text-dark-text-secondary"
                data-testid="calendar-activities-error"
              >
                {
                  {
                    session: "Recorded activities could not be loaded because your session expired.",
                    network: "Recorded activities could not be loaded. Check your connection and try again.",
                    server: "Recorded activities could not be loaded right now.",
                  }[activitiesError]
                }
                <button
                  type="button"
                  onClick={fetchMonthActivities}
                  className="ml-2 underline text-brand-primary"
                >
                  Retry
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 sm:p-5 border-b border-dark-border bg-dark-surface-1/40">
              <MetricCard
                label="Activities"
                value={activitiesLoading ? "—" : monthTotals.count}
                icon={activitiesMetric.icon}
                accent={TOKENS.analytics.green}
                soft={activitiesMetric.soft}
                className="shadow-app-sm"
                testId="calendar-month-activities"
              />
              <MetricCard
                label="Distance"
                value={activitiesLoading ? "—" : fmtDistance(monthTotals.distance_m, unitSystem)}
                icon={distanceMetric.icon}
                accent={distanceMetric.accent}
                soft={distanceMetric.soft}
                className="shadow-app-sm"
                testId="calendar-month-distance"
              />
              <MetricCard
                label="Time"
                value={activitiesLoading ? "—" : fmtDurationShort(monthTotals.duration_s)}
                icon={durationMetric.icon}
                accent={durationMetric.accent}
                soft={durationMetric.soft}
                className="shadow-app-sm"
                testId="calendar-month-time"
              />
            </div>

            <div className="px-3 py-3 sm:px-5 sm:py-4 md:px-6 md:py-4" data-testid="calendar-grid">
              {activitiesLoading && (
                <p
                  className="mb-2 text-[10px] uppercase tracking-widest text-dark-text-muted"
                  data-testid="calendar-activities-loading"
                >
                  Loading recorded activities…
                </p>
              )}
              <Calendar
                key={`${selectedYear}-${selectedMonth}`}
                mode="single"
                defaultMonth={displayMonth}
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) setSelectedDate(date);
                }}
                showOutsideDays
                className="w-full p-0"
                classNames={{
                  months: "flex flex-col w-full",
                  month: "w-full space-y-2",
                  caption: "hidden",
                  nav: "hidden",
                  table: "w-full border-collapse",
                  head_row: "flex w-full mb-0.5",
                  head_cell:
                    "flex-1 text-center text-[10px] sm:text-[11px] font-medium tracking-[0.18em] uppercase text-dark-text-muted py-1",
                  row: "flex w-full mt-0.5 sm:mt-1",
                  cell: "flex-1 min-w-0 h-10 sm:h-11 md:h-14 flex items-center justify-center p-0",
                  day: [
                    "w-9 h-9 sm:w-10 sm:h-10",
                    "p-0 font-mono text-sm font-medium rounded-lg",
                    "inline-flex items-center justify-center",
                    "hover:bg-dark-surface-3/80 transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/50",
                    "aria-selected:opacity-100",
                  ].join(" "),
                  day_today:
                    "ring-1 ring-inset ring-brand-primary/35 bg-dark-surface-2/50 font-semibold text-dark-text-primary",
                  day_outside: "text-dark-text-muted/45",
                  day_disabled: "text-dark-text-muted/30",
                  day_selected:
                    "!bg-brand-primary !text-white hover:!bg-brand-primary-hover shadow-app-sm ring-0",
                  day_hidden: "invisible",
                }}
                components={{
                  DayContent: (props) => (
                    <RideDayContent
                      {...props}
                      rideDateKeys={rideDateKeys}
                      plannedDateKeys={plannedDateKeys}
                      restDateKeys={restDateKeys}
                    />
                  ),
                }}
              />
            </div>
          </section>

          <SelectedDayDetails
            selectedDate={selectedDate}
            sessions={dayPlannedSessions}
            dayActivities={dayActivities}
            activities={activities}
            unitSystem={unitSystem}
            onEditSession={setEditingSession}
            isRestDay={Boolean(selectedDateKey && restDateKeys.has(selectedDateKey))}
          />

          {hasActivePlan ? (
            <ActivePlanOverview
              activePlan={activePlan}
              onEditSession={setEditingSession}
            />
          ) : (
            <NoActivePlanNotice />
          )}
      </>
      {editingSession && (
        <PlannedSessionEditor
          session={editingSession}
          onClose={() => setEditingSession(null)}
          onSaved={() => {
            setEditingSession(null);
            reloadActivePlan();
          }}
        />
      )}
    </div>
  );
};

const SelectedDayDetails = ({
  selectedDate,
  sessions,
  dayActivities,
  activities,
  unitSystem,
  onEditSession,
  isRestDay = false,
}) => {
  if (!selectedDate) {
    return (
      <section className="mb-8" data-testid="calendar-day-activities">
        <EmptyState
          compact
          title="Select a date"
          description="Choose a day on the calendar to see planned training and recorded activities for that date."
          icon={CalendarRange}
          className="bg-dark-surface-1/50"
        />
      </section>
    );
  }

  const planned = sessions || [];
  const recorded = dayActivities || [];
  const anyCompleted = planned.some((session) => session.is_completed);
  const recordedHeading = planned.length > 0 && recorded.length > 0 && !anyCompleted
    ? (recorded.length === 1 ? "Other recorded activity" : "Other recorded activities")
    : "Recorded activities";
  const dateLabel = fmtDate(`${toDateKey(selectedDate)}T12:00:00`);

  return (
    <section className="mb-8 min-w-0" data-testid="calendar-day-activities">
      <SectionHeader
        title={`Activities for ${dateLabel}`}
        description={
          planned.length > 0 && recorded.length > 0
            ? `${planned.length} planned · ${recorded.length} recorded`
            : planned.length > 0
              ? `${planned.length} planned session${planned.length === 1 ? "" : "s"}`
              : recorded.length > 0
                ? `${recorded.length} recorded activit${recorded.length === 1 ? "y" : "ies"}`
                : isRestDay
                  ? "Rest day. No workout is planned."
                  : "No planned sessions or recorded activities for this date."
        }
      />

      <PlannedTrainingForDay
        sessions={planned}
        onEditSession={onEditSession}
      />

      {planned.length === 0 && isRestDay && (
        <p className="mb-4 text-sm text-dark-text-secondary" data-testid="calendar-rest-day">
          Rest day. No workout is planned for {dateLabel}.
        </p>
      )}

      {planned.length === 0 && !isRestDay && recorded.length > 0 && (
        <p className="mb-4 text-sm text-dark-text-muted">
          No training session was planned for this date.
        </p>
      )}

      <div className="mb-6" data-testid="calendar-recorded-activities">
        <h3 className="card-label mb-3">
          {recordedHeading}
        </h3>
        {recorded.length === 0 ? (
          <EmptyState
            compact
            title="No recorded activity"
            description="Nothing was logged on this date."
            className="bg-dark-surface-1/40"
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {recorded.map((activity, index) => (
              <ActivityCard
                key={activity.id}
                activity={activity}
                index={index}
                unitSystem={unitSystem}
              />
            ))}
          </div>
        )}
      </div>

      {planned.map((session) => (
        <PlanVsActual
          key={`compare-${session.session_id}`}
          session={session}
          activities={activities}
          unitSystem={unitSystem}
        />
      ))}
    </section>
  );
};

const PlannedTrainingForDay = ({ sessions, onEditSession }) => {
  if (!sessions || sessions.length === 0) return null;
  return (
    <div
      className="mb-6 space-y-4"
      data-testid="calendar-planned-sessions"
    >
      <h3 className="card-label">Planned training</h3>
      <ul className="space-y-4">
        {sessions.map((session) => {
          const purpose = formatObjective(session.objective || session.role);
          const prescription = session.intensity_summary
            || "Complete the planned duration at the session effort.";
          const changeLabel = sessionChangeLabel(session);
          const statusLabel = plannedStatusLabel(session);
          return (
            <li
              key={session.session_id}
              className="app-card-elevated p-4 sm:p-5 text-sm text-dark-text-primary"
              data-testid={`planned-session-${session.session_id}`}
              data-session-id={session.session_id}
              data-scheduled-date={session.scheduled_date}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <SportBadge sport={session.sport} />
                    <PlanSessionStatus session={session} fallback={statusLabel} />
                  </div>
                  <div className="font-heading text-lg sm:text-xl tracking-wider uppercase text-dark-text-primary break-words">
                    {session.workout_name || session.workout_code || "Session"}
                  </div>
                  <div className="font-mono text-sm text-dark-text-secondary">
                    {plannedDurationLabel(session)}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <WorkoutDownloadMenu
                    session={session}
                    testId={`planned-session-download-${session.session_id}`}
                  />
                  {session.is_editable && onEditSession && (
                    <button
                      type="button"
                      onClick={() => onEditSession(session)}
                      className="btn-secondary-app text-[10px] uppercase tracking-widest shrink-0"
                      data-testid={`planned-session-edit-${session.session_id}`}
                    >
                      Edit workout
                    </button>
                  )}
                </div>
              </div>
              {purpose && purpose !== "—" && (
                <div className="mt-4 pt-4 border-t border-dark-border/70">
                  <div className="card-label">Purpose</div>
                  <div className="mt-1 text-sm text-dark-text-secondary">{purpose}</div>
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-dark-border/70">
                <div className="card-label">Current prescription</div>
                <div className="mt-1 text-sm text-dark-text-secondary">{prescription}</div>
              </div>
              <details className="mt-4 pt-4 border-t border-dark-border/70 group">
                <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                  <div className="flex items-center justify-between gap-2">
                    <div className="card-label">Full workout plan</div>
                    <span className="text-[10px] uppercase tracking-widest text-dark-text-muted group-open:text-brand-primary">
                      Details
                    </span>
                  </div>
                </summary>
                <div className="mt-3" data-testid={`planned-session-prescription-${session.session_id}`}>
                  <SessionPrescriptionView session={session} />
                </div>
              </details>
              {changeLabel && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <StatusBadge variant="load">{changeLabel}</StatusBadge>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const PlanVsActual = ({ session, activities, unitSystem }) => {
  if (!session?.comparison_execution_status) return null;
  const plannedName = session.workout_name || session.workout_code || "Planned session";
  const plannedLine = [plannedName, plannedDurationLabel(session)].filter(Boolean).join(" · ");
  const matched = findActivityById(activities, session.completed_activity_id);
  const actualLine = matched
    ? [
      deriveActivityName(matched),
      sportDisplayLabel(matched),
      fmtDurationShort(matched.duration_seconds),
      fmtDistance(matched.total_distance_m, unitSystem),
    ].filter(Boolean).join(" · ")
    : null;
  const result = plannedStatusLabel(session);
  const actualLabel = session.is_completed ? "Completed" : "Actual";

  return (
    <div
      className="mb-6 app-card p-4 sm:p-5"
      data-testid={`calendar-plan-vs-actual-${session.session_id}`}
    >
      <h3 className="card-label mb-4">Plan vs actual</h3>
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="card-label">Planned</dt>
          <dd className="mt-1 text-dark-text-primary">{plannedLine}</dd>
        </div>
        {actualLine && (
          <div>
            <dt className="card-label">{actualLabel}</dt>
            <dd className="mt-1 text-dark-text-primary">{actualLine}</dd>
          </div>
        )}
        <div>
          <dt className="card-label">Result</dt>
          <dd className="mt-1">
            <PlanSessionStatus session={session} fallback={result} />
          </dd>
        </div>
      </dl>
    </div>
  );
};

const NoActivePlanNotice = () => (
  <section className="mb-8" data-testid="calendar-no-active-plan">
    <SectionHeader title="Active Training Plan" />
    <EmptyState
      compact
      title="No active training plan"
      description="Set up a training plan with your AI Coach to see planned sessions on the calendar."
      icon={ClipboardList}
      action={(
        <a
          href="/coach"
          className="btn-primary-app text-xs uppercase tracking-widest"
          data-testid="calendar-setup-training-plan"
        >
          Set up a training plan
        </a>
      )}
      className="app-card"
    />
  </section>
);

const PlanSessionStatus = ({ session, fallback }) => {
  const label = session?.plan_status_label || fallback;
  if (!label) return null;
  const variant = sessionStatusVariant(session, label);
  const displayLabel = session.is_completed ? `✓ ${label}` : label;
  return (
    <StatusBadge
      variant={variant}
      data-testid={`plan-session-status-${session.session_id}`}
    >
      {displayLabel}
    </StatusBadge>
  );
};

const ActivePlanSessionRow = ({ session, onEditSession }) => {
  const changeLabel = sessionChangeLabel(session);
  const objective = formatObjective(session.objective || session.role);
  const statusLabel = plannedStatusLabel(session);

  return (
    <li
      className={[
        "app-card-elevated p-3 sm:p-4 transition-colors",
        session.is_completed ? "opacity-90" : "",
      ].join(" ")}
      data-testid={`active-plan-session-${session.session_id}`}
      data-completed={session.is_completed ? "true" : "false"}
      data-editable={session.is_editable ? "true" : "false"}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-mono text-dark-text-muted shrink-0">
              {formatDate(session.scheduled_date)}
            </span>
            <SportBadge sport={session.sport} />
            <PlanSessionStatus session={session} fallback={statusLabel} />
          </div>
          <div className="font-heading text-base sm:text-lg tracking-wider uppercase text-dark-text-primary break-words">
            {session.workout_name || session.workout_code || "Session"}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-dark-text-secondary">
            <span className="font-mono">{plannedDurationLabel(session)}</span>
            {objective && objective !== "—" && (
              <span className="text-dark-text-muted">· {objective}</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {changeLabel && (
              <StatusBadge variant="load">{changeLabel}</StatusBadge>
            )}
            {session.difficulty_adjustment && session.difficulty_adjustment !== "AS_PLANNED" && (
              <StatusBadge variant="load">
                {session.difficulty_adjustment.replace(/_/g, " ").toLowerCase()}
              </StatusBadge>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 self-start">
          <WorkoutDownloadMenu
            session={session}
            testId={`active-plan-session-download-${session.session_id}`}
          />
          {session.is_editable && onEditSession && (
            <button
              type="button"
              onClick={() => onEditSession(session)}
              className="btn-secondary-app text-[10px] uppercase tracking-widest shrink-0"
              data-testid={`active-plan-session-edit-${session.session_id}`}
            >
              Edit
            </button>
          )}
        </div>
      </div>
      <details className="mt-3 group">
        <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
          <span className="text-[10px] uppercase tracking-widest text-dark-text-muted group-open:text-brand-primary">
            Full workout plan
          </span>
        </summary>
        <div className="mt-3" data-testid={`active-plan-session-prescription-${session.session_id}`}>
          <SessionPrescriptionView session={session} />
        </div>
      </details>
    </li>
  );
};

const ActivePlanOverview = ({ activePlan, onEditSession }) => {
  if (!activePlan || !Array.isArray(activePlan.weeks) || activePlan.weeks.length === 0) {
    return null;
  }
  const totalSessions = activePlan.weeks.reduce(
    (acc, w) => acc + (w.sessions?.length || 0), 0,
  );
  const totalMinutes = activePlan.weeks.reduce(
    (acc, w) => acc + (w.sessions || []).reduce(
      (a, s) => a + (typeof s.duration_minutes === "number" ? s.duration_minutes : 0),
      0,
    ),
    0,
  );
  const completedCount = activePlan.weeks.reduce(
    (acc, w) => acc + (w.sessions || []).filter((s) => s.is_completed).length,
    0,
  );
  const remainingCount = totalSessions - completedCount;
  const completionRatio = totalSessions > 0
    ? Math.round((completedCount / totalSessions) * 100)
    : null;
  const { start: displayStartDate, end: displayEndDate } = activePlanBlockDates(activePlan);

  const startMetric = getMetricVisual("neutral");
  const sessionsMetric = getMetricVisual("brand");
  const timeMetric = getMetricVisual("duration");
  const completedMetric = getMetricVisual("brand");
  const remainingMetric = getMetricVisual("neutral");

  return (
    <section
      className="app-card mb-8 p-4 sm:p-6"
      data-testid="calendar-active-plan-overview"
    >
      <SectionHeader
        title="Active Training Plan"
        description="Your current block with weekly sessions and completion status."
        actions={(
          <a
            href="/coach"
            className="btn-secondary-app text-[10px] uppercase tracking-widest"
            data-testid="calendar-adjust-my-plan"
          >
            Adjust my plan
          </a>
        )}
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <MetricCard
          label="Start"
          value={formatDate(displayStartDate) || "—"}
          icon={startMetric.icon}
          className="!p-3 sm:!p-4 shadow-app-sm col-span-1"
        />
        <MetricCard
          label="End"
          value={formatDate(displayEndDate) || "—"}
          icon={startMetric.icon}
          className="!p-3 sm:!p-4 shadow-app-sm col-span-1"
        />
        <MetricCard
          label="Planned sessions"
          value={totalSessions}
          icon={sessionsMetric.icon}
          accent={TOKENS.analytics.blue}
          soft={sessionsMetric.soft}
          className="!p-3 sm:!p-4 shadow-app-sm col-span-1"
        />
        <MetricCard
          label="Planned time"
          value={totalMinutes > 0 ? totalMinutes : "—"}
          unit={totalMinutes > 0 ? "min" : undefined}
          icon={timeMetric.icon}
          accent={timeMetric.accent}
          soft={timeMetric.soft}
          className="!p-3 sm:!p-4 shadow-app-sm col-span-1"
        />
        <MetricCard
          label="Completed"
          value={completedCount}
          icon={completedMetric.icon}
          accent={TOKENS.analytics.green}
          soft={completedMetric.soft}
          supportingText={totalSessions > 0 ? `${completedCount} of ${totalSessions}` : undefined}
          className="!p-3 sm:!p-4 shadow-app-sm col-span-1"
          testId="calendar-plan-completion-totals"
        />
        <MetricCard
          label="Remaining"
          value={remainingCount > 0 ? remainingCount : "—"}
          icon={remainingMetric.icon}
          supportingText={completionRatio != null ? `${completionRatio}% complete` : undefined}
          className="!p-3 sm:!p-4 shadow-app-sm col-span-1"
        />
      </div>

      <div className="space-y-5 sm:space-y-6">
        {activePlan.weeks.map((week) => {
          const stats = weekSessionStats(week);
          return (
            <div
              key={week.week_number}
              className="app-card-elevated p-4 sm:p-5"
              data-testid={`active-plan-week-${week.week_number}`}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between pb-4 mb-4 border-b border-dark-border">
                <div className="min-w-0">
                  <h3 className="font-heading text-lg sm:text-xl tracking-wider uppercase text-dark-text-primary">
                    {formatWeekHeader(week)}
                  </h3>
                  <p className="text-xs font-mono text-dark-text-muted mt-1">
                    {formatDate(week.week_start_date)} — {formatDate(week.week_end_date)}
                  </p>
                </div>
                {stats.count > 0 && (
                  <div className="flex flex-wrap gap-3 text-xs text-dark-text-secondary shrink-0">
                    <span>{stats.count} session{stats.count !== 1 ? "s" : ""}</span>
                    {stats.minutes > 0 && (
                      <span className="font-mono">{stats.minutes} min planned</span>
                    )}
                  </div>
                )}
              </div>
              {(!week.sessions || week.sessions.length === 0) && !(week.rest_dates || []).length ? (
                <p className="text-sm text-dark-text-muted italic">
                  No sessions this week.
                </p>
              ) : (
                <ul className="space-y-3">
                  {planDatedRows(week, week.sessions).map((row) => (
                    row.kind === "rest" ? (
                      <li
                        key={`rest-${row.date}`}
                        className="text-sm text-dark-text-secondary"
                        data-testid="calendar-plan-rest-day"
                        data-date={row.date}
                      >
                        {formatDate(row.date)} — Rest
                      </li>
                    ) : (
                      <ActivePlanSessionRow
                        key={row.session.session_id}
                        session={row.session}
                        onEditSession={onEditSession}
                      />
                    )
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
